import axios from 'axios';
import {Local, Session, STORAGE_KEYS} from 'utils/storage';
import qs from 'qs';
import other from './other';
import {wrapEncryption, encryptRequestParams, decrypt} from './apiCrypto';
import i18n from '@/i18n/index';
import { toast } from 'vue-sonner';

// 常用header
export const CommonHeaderEnum = {
    'TENANT_ID': 'TENANT-ID',
    'ENC_FLAG': 'Enc-Flag',
    'AUTHORIZATION': 'Authorization',
    'VERSION': 'VERSION',
}

/**
 * 创建并配置一个 Axios 实例对象
 */
const service= axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: 50000, // 全局超时时间
    paramsSerializer: {
        serialize: (params) => {
            return qs.stringify(params, {arrayFormat: 'repeat'});
        },
    },
});

/**
 * Axios请求拦截器，对请求进行处理
 * 1. 序列化get请求参数
 * 2. 统一增加Authorization和TENANT-ID请求头
 * 3. 自动适配单体、微服务架构不同的URL
 * @param config AxiosRequestConfig对象，包含请求配置信息
 */
service.interceptors.request.use(
    (config) => {
        // 前端来源标识头：供网关域名白名单兜底识别前端真实 Host（vite 代理同源化场景 Origin/Referer 会双丢）
        config.headers['X-Client-Host'] = window.location.host;

        // 从 i18n 获取当前语言设置 Accept-Language
        const locale = i18n.global.locale.value || Local.get(STORAGE_KEYS.LOCALE) || 'en-US';
        config.headers['Accept-Language'] = locale;

        // 统一增加Authorization请求头, skipToken 跳过增加token
        const token = Session.getToken();
        if (token && !config.headers?.skipToken) {
            config.headers[CommonHeaderEnum.AUTHORIZATION] = `Bearer ${token}`;
        }

        // 统一增加TENANT-ID请求头, skipTenant 跳过增加租户ID
        const tenantId = Session.getTenant();
        if (tenantId && !config.headers?.skipTenant) {
            config.headers[CommonHeaderEnum.TENANT_ID] = tenantId;
        }

        // 增加 gray_version 请求头
        const version = import.meta.env.VITE_GRAY_VERSION;
        if (version) {
            config.headers[CommonHeaderEnum.VERSION] = version;
        }

        // 请求报文加密 ，如果请求头中不包含 ENC_FLAG ： false 则加密
        if (config.data && !config.headers[CommonHeaderEnum.ENC_FLAG]) {
            config.data = wrapEncryption(config.data);
        }

        // 如果是 GET ，加密 config.param 的每一个参数，并URLencode
        if (config.method === 'get' && config.params) {
            config.params = encryptRequestParams(config.params);
        }

        // 添加终端类型
        const isToc = import.meta.env.VITE_IS_TOC;
        if (isToc === "true") {
            config.params = encryptRequestParams(config.params);
            config.headers['CLIENT-TOC'] = 'Y';
            // 设备类型：mobile / desktop
            const device = document.querySelector('html')?.getAttribute('device') || 'desktop';
            config.headers['X-Device-Type'] = device;
        }
        // 自动适配单体和微服务架构不同的URL
        config.url = other.adaptationUrl(config.url);

        // 处理完毕，返回config对象
        return config;
    },
    (error) => {
        // 对请求错误进行处理
        return Promise.reject(error);
    }
);

/**
 * 响应拦截器处理函数
 * @param response 响应结果
 * @returns 如果响应成功，则返回响应的data属性；否则，抛出错误或者执行其他操作
 */
const handleResponse = (response) => {
    // 密码过期拦截：后端返回 HTTP 200 + code 4011
    if (response.data.code === 4011) {
        console.log('[密码过期] 后端返回 4011，密码已过期', response.data);
        // 触发全局密码过期弹窗（防重复触发）
        if (window.__PASSWORD_EXPIRED_CALLBACK && !window.__PASSWORD_EXPIRED_TRIGGERED) {
            window.__PASSWORD_EXPIRED_TRIGGERED = true;
            window.__PASSWORD_EXPIRED_CALLBACK();
        }
        return Promise.reject(response.data);
    }

    if (response.data.code === 1) {
        throw response.data;
    }

    // 针对密文返回解密
    if (response.data.encryption) {
        response.data = decrypt(response.data.encryption);
        return response.data;
    }

    return response.data;
};

/**
 * 添加 Axios 的响应拦截器，用于全局响应结果处理
 */
service.interceptors.response.use(handleResponse, (error) => {
    const status = Number(error?.response?.status) || 200;
    if (status === 423) {
        return Promise.reject({msg: i18n.global.t('common.demoEnvOnly')});
    }

    if (status === 424) {
        // 触发全局 token 过期回调（防重复触发）
        if (window.__TOKEN_EXPIRED_CALLBACK && !window.__TOKEN_EXPIRED_TRIGGERED) {
            window.__TOKEN_EXPIRED_TRIGGERED = true;
            window.__TOKEN_EXPIRED_CALLBACK();
        }
        return Promise.reject({msg: i18n.global.t('common.tokenExpired')});
    }

    if (status === 425) {
        // 域名未授权访问，跳转错误页阻断所有后续请求
        if (!window.__DOMAIN_INVALID_TRIGGERED) {
            window.__DOMAIN_INVALID_TRIGGERED = true;
            Session.clear();
            localStorage.removeItem("isLogined");
            Local.remove(STORAGE_KEYS.TENANT_ID);
            if (window.location.pathname !== '/domain-invalid') {
                window.location.replace('/domain-invalid');
            }
        }
        return Promise.reject({msg: i18n.global.t('common.domainNotAuthorized')});
    }

    if (status === 426) {
        // 站点已停用，跳站点不可用页阻断访问（对前台用户隐藏内部概念）
        if (!window.__TENANT_EXPIRED_TRIGGERED) {
            window.__TENANT_EXPIRED_TRIGGERED = true;
            Session.clear();
            localStorage.removeItem("isLogined");
            Local.remove(STORAGE_KEYS.TENANT_ID);
            toast.error(i18n.global.t('common.siteUnavailable'));
            const target = '/domain-invalid?reason=site-unavailable';
            if (!window.location.href.includes('reason=site-unavailable')) {
                window.location.replace(target);
            }
        }
        return Promise.reject({msg: i18n.global.t('common.siteUnavailable')});
    }

    // 针对密文返回解密
    if (error.response?.data?.encryption) {
        error.response.data = decrypt(error.response?.data.encryption);
    }

    // error.response 为空 = 后端连不上/超时（网络层错误），运行时业务接口静默会让人一脸懵，弹一条服务端出错提示
    if (!error.response) {
        toast.error(i18n.global.t('common.networkError'));
    }

    return Promise.reject(error.response?.data);
});

// 导出 axios 实例
export default service;
